import React, { useEffect, useState } from 'react'
import { HeaderCP } from 'submodules/nerit-framework-ui/common/components/screen/layout/header/HeaderCP'
import { LayoutCP } from 'submodules/nerit-framework-ui/common/components/screen/layout/layout/LayoutCP'
import { ContentCP } from 'submodules/nerit-framework-ui/common/components/screen/layout/content/ContentCP'
import { TitleCP } from 'submodules/nerit-framework-ui/common/components/title/TitleCP'
import { RowCP } from 'submodules/nerit-framework-ui/common/components/grid/row/RowCP'
import { ColumnCP } from 'submodules/nerit-framework-ui/common/components/grid/column/ColumnCP'
import { WelcomeContentCP } from 'submodules/nerit-framework-ui/common/components/welcome-content/WelcomeContentCP'
import { IconICP } from 'submodules/nerit-framework-ui/common/components/icon/inner/IconICP'
import { NotificationHelper } from 'submodules/nerit-framework-ui/common/components/notification/inner/NotificationHelper'
import { useFormStateManager } from 'submodules/nerit-framework-ui/common/form-state-manager/UseFormStateManager'
import { FormStateManagerUtils } from 'submodules/nerit-framework-ui/common/form-state-manager/FormStateManagerUtils'
import { RequestHelper } from 'submodules/nerit-framework-utils/sdk-utils/request-manager/RequestHelper'
import { IApiReturn } from 'submodules/nerit-framework-utils/sdk-utils/request-manager/types/IApiReturn'
import { ISearchResult } from 'submodules/nerit-framework-utils/sdk-utils/dtos/interfaces/ISearchResult'
import { NameAndCodeResponseDTO } from 'submodules/nerit-framework-utils/sdk-utils/dtos/response/NameAndCodeResponseDTO'
import { SamplingRequests } from 'submodules/nerit-aquatec-sdk/services/samplings/SamplingRequests'
import { RegulationRequests } from 'submodules/nerit-aquatec-sdk/services/regulation/RegulationRequests'
import { LocationRequests } from 'submodules/nerit-aquatec-sdk/services/location/LocationRequests'
import { LocationResponseDTO } from 'submodules/nerit-aquatec-sdk/services/location/dtos/responses/LocationResponseDTO'
import { AppStateUtils } from 'app/redux/AppStateUtils'
import { DashboardFiltersFormModel } from 'app/screens/screen-home/inner/DashboardFiltersFormModel'
import { DashboardFilterTP } from 'app/screens/screen-home/inner/DashboardFilterTP'
import { DashboardDefaultsUtils } from 'app/screens/screen-home/inner/DashboardDefaultsUtils'
import { DashboardFiltersHeaderCP } from 'app/screens/screen-home/components/DashboardFiltersHeaderCP'
import { DashboardMonitoringStatusCP } from 'app/screens/screen-home/components/DashboardMonitoringStatusCP'
import { DashboardNonConformityCardCP } from 'app/screens/screen-home/components/DashboardNonConformityCardCP'
import { DashboardQAQCCardCP } from 'app/screens/screen-home/components/DashboardQAQCCardCP'
import { DashboardCollectionPerformanceCardCP } from 'app/screens/screen-home/components/DashboardCollectionPerformanceCardCP'
import { DashboardSpatialTemporalCP } from 'app/screens/screen-home/components/DashboardSpatialTemporalCP'

const MAX_CAMPAIGN_LOCATIONS = 9999

/**
 * Dashboard (Página Inicial): visão consolidada do monitoramento conforme o filtro do cabeçalho.
 */
export function ScreenHome(): JSX.Element {

    const schemaSlug = AppStateUtils.getDomainData()?.slug

    const [formModel, setFormModel] = useState<DashboardFiltersFormModel>(new DashboardFiltersFormModel())
    const formStateManager = useFormStateManager(formModel)

    const [appliedFilter, setAppliedFilter] = useState<DashboardFilterTP>()
    const [isApplying, setIsApplying] = useState<boolean>(true)

    useEffect(() => { void init() }, [])

    /**
     * Monta o filtro padrão (campanha de água superficial, último mês e CONAMA 357 Classe 2) e já o aplica.
     */
    async function init(): Promise<void> {

        try {
            const [samplingsResponse, regulationsResponse] = await Promise.all([
                RequestHelper.runRequest<IApiReturn<ISearchResult<NameAndCodeResponseDTO>>>(SamplingRequests.names({})),
                RequestHelper.runRequest<IApiReturn<ISearchResult<NameAndCodeResponseDTO>>>(RegulationRequests.getNameList()),
            ])

            const sampling = DashboardDefaultsUtils.findDefaultSampling(samplingsResponse.data.data?.list ?? [], schemaSlug)
            const regulation = DashboardDefaultsUtils.findDefaultRegulation(regulationsResponse.data.data?.list ?? [])

            const defaultValues = new DashboardFiltersFormModel({
                samplingCode: sampling?.code,
                regulationCode: regulation?.code,
                regulationName: regulation?.name,
                dateRange: DashboardDefaultsUtils.getDefaultDateRange(),
            })
            setFormModel(defaultValues)

            if (!sampling || !regulation) {
                setIsApplying(false)
                return
            }

            await applyFilter(defaultValues)
        } catch {
            NotificationHelper.error(NotificationHelper.DEFAULT_ERROR_GET_MESSAGE)
            setIsApplying(false)
        }
    }

    /**
     */
    async function onApply(): Promise<void> {

        if (!await FormStateManagerUtils.validateRequiredFields(formStateManager))
            return

        await applyFilter(formStateManager.getFormValues()!)
    }

    /**
     * Busca os pontos da campanha e publica o filtro para os cards.
     */
    async function applyFilter(values: DashboardFiltersFormModel): Promise<void> {

        setIsApplying(true)
        try {
            const response = await RequestHelper.runRequest<IApiReturn<ISearchResult<LocationResponseDTO>>>(
                LocationRequests.getList({ samplingCode: values.samplingCode, page: 1, itemsPerPage: MAX_CAMPAIGN_LOCATIONS }),
            )
            const locations = response.data.data?.list ?? []

            setAppliedFilter({
                samplingCode: values.samplingCode!,
                regulationCode: values.regulationCode!,
                regulationName: values.regulationName,
                dateRange: values.dateRange!,
                locations,
                locationCodes: locations.map((location) => location.code),
            })
        } catch {
            NotificationHelper.error(NotificationHelper.DEFAULT_ERROR_GET_MESSAGE)
        } finally {
            setIsApplying(false)
        }
    }

    return (
        <LayoutCP
            titleBrowser={'Página Inicial'}
            header={<HeaderCP title={'Dashboard'}/>}
            content={
                <ContentCP overflowVertical={true}>
                    <DashboardFiltersHeaderCP
                        schemaName={DashboardDefaultsUtils.formatSchemaName(schemaSlug)}
                        formStateManager={formStateManager}
                        onApply={onApply}
                        isLoading={isApplying}
                    />

                    {
                        !appliedFilter
                            ? (
                                !isApplying &&
                                <WelcomeContentCP
                                    message={'Selecione a campanha, o período e a norma e clique em Aplicar'}
                                    icon={<IconICP iconName={'filter'} size={42}/>}
                                />
                            )
                            : (
                                <>
                                    <SectionTitleCP text={'Situação do Monitoramento'}/>
                                    <DashboardMonitoringStatusCP filter={appliedFilter}/>

                                    <SectionTitleCP text={'Qualidade da Informação'}/>
                                    <RowCP>
                                        <ColumnCP size={12} xs={24} lg={12}>
                                            <DashboardNonConformityCardCP filter={appliedFilter}/>
                                        </ColumnCP>
                                        <ColumnCP size={12} xs={24} lg={12}>
                                            <DashboardQAQCCardCP filter={appliedFilter}/>
                                        </ColumnCP>
                                    </RowCP>

                                    <SectionTitleCP text={'Desempenho de Coleta'}/>
                                    <DashboardCollectionPerformanceCardCP dateRange={appliedFilter.dateRange}/>

                                    <SectionTitleCP text={'Relação Espacial e Temporal'}/>
                                    <DashboardSpatialTemporalCP filter={appliedFilter}/>
                                </>
                            )
                    }
                </ContentCP>
            }
        />
    )
}

function SectionTitleCP(props: { text: string }): JSX.Element {
    return (
        <TitleCP marginLeft={20} marginTop={16} marginBottom={4} textSize={'normal'} underLine={true}>
            {props.text}
        </TitleCP>
    )
}
